import React from 'react'
import { Text, ScrollView, TouchableOpacity, View } from 'react-native'
import { OPLCommon, OPLDocumentConst, OPLSharedStyles } from '../../opl'
import { SectionCol, SectionContent, SectionRow } from '@pulseops/submission/common'
import {
  AppContext,
  assets,
  AuthService,
  Checkbox,
  CheckboxTable,
  CheckboxTableColum,
  CSService,
  DatePicker,
  ErrorHandling,
  Permission,
  PulseOpsFormat,
  SelectSearch,
  sharedStyle,
  SortFieldInfo
} from '@pulseops/common'
import { useTranslation } from 'react-i18next'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { pipe } from 'fp-ts/lib/function'
import { ZIO } from '@mxt/zio'
import { useLoading } from '@mxt/zio-react'
import { useIsFocused } from '@react-navigation/native'
import moment from 'moment'
import { CSStackContext } from '../CSStackContext'
import * as O from 'fp-ts/lib/Option'
import * as A from 'fp-ts/lib/Array'
import OPLDeleteModal from '../../opl/opl-common/OPLDeleteModal'
import { CSProcessGuidelineDocContext, CSProcessGuidelineDocForm } from '../cs-process-guideline-document'

type CSInternalProcessGuidelineDocListProps = {
  processType: string
}

export const CSInternalProcessGuidelineDocList = (props: CSInternalProcessGuidelineDocListProps) => {
  const { t } = useTranslation()
  const isFocused = useIsFocused()
  const [hasEnableDelete, setHasEnableDelete] = React.useState(false)
  const [hasEnableViewDetail, setHasEnableViewDetail] = React.useState(false)
  const [hasEnableExtract, setHasEnableExtract] = React.useState(false)
  const [isOpenDeleteModal, setIsOpenDeleteModal] = React.useState(false)
  const [pageNum, setPageNum] = React.useState<number>(0)
  const [pageSize, setPageSize] = React.useState<number>(10)
  const [total, setTotal] = React.useState<number>(0)
  const [sortItem, setSortItem] = React.useState<SortFieldInfo>({ field: '', order: 'asc' })
  const [isLoading, bindLoader] = useLoading(false)
  const { showToast, showGlobalLoading } = React.useContext(AppContext.AppContextInstance)
  const { userPermissions } = React.useContext(CSStackContext)
  const { docTypeList, docTypeDetailList } = React.useContext(CSProcessGuidelineDocContext)
  const [processGuidelineDocList, setProcessGuidelineDocList] = React.useState<Array<CSService.GADDocumentInfo>>([])
  const [userEmail, setUserEmail] = React.useState<string>('')
  const [docTypeDetailArr, setDocTypeDetailArr] = React.useState<Array<CSService.DocTypeDetailInfo>>([])
  const { control, getValues, setValue, trigger, watch } =
    useForm<CSProcessGuidelineDocForm.CSProcessGuidelineDocListData>({
      defaultValues: {
        from: null,
        to: null,
        docID: undefined,
        isSelectedAll: false,
        guidelineDoclist: []
      },
      mode: 'onChange'
    })

  const guidelineDocFieldArr = useFieldArray<CSProcessGuidelineDocForm.CSProcessGuidelineDocListData>({
    control: control,
    name: 'guidelineDoclist'
  })
  const displayedColumns: CheckboxTableColum[] = [
    {
      title: '',
      fieldName: '',
      disabled: true,
      headerColumnRender: (list: any) => {
        return (
          <Controller
            control={control}
            name="isSelectedAll"
            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
              return (
                <Checkbox
                  onChange={(val) => {
                    onChange(val)
                    onSelectedAllEvent(val, list.length)
                  }}
                  onBlur={onBlur}
                  value={value}
                  checkBoxStyle={OPLSharedStyles.checkboxContainer}
                />
              )
            }}
          />
        )
      },
      rowRender: (renderProps) => {
        return (
          <Controller
            control={control}
            name={`guidelineDoclist.${renderProps.index}.isSelectedItem`}
            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
              return (
                <Checkbox
                  key={`guidelineDoclist_isSelectedItem_${renderProps.index}`}
                  onChange={(val) => {
                    onChange(val)
                    setValue(
                      `guidelineDoclist.${renderProps.index}.historyID`,
                      processGuidelineDocList[renderProps.index].id
                    )
                    onSelectedItemEvent()
                  }}
                  onBlur={onBlur}
                  value={value}
                  checkBoxStyle={OPLSharedStyles.checkboxContainer}
                />
              )
            }}
          />
        )
      }
    },
    {
      title: t('OPL_CS:CS:Document'),
      fieldName: 'fileName'
    },
    {
      title: t('OPL_CS:CS:DocIDDocumentType'),
      fieldName: 'docType'
    },
    {
      title: t('OPL_CS:CS:DocumentTypeDetail'),
      fieldName: 'docTypeDetail'
    },
    {
      title: t('OPL_CS:CS:ModifiedDate'),
      fieldName: 'modifiedDate'
    },
    {
      title: t('OPL_CS:CS:EffectiveDate'),
      fieldName: 'effectiveDate'
    },
    {
      title: t('OPL_CS:OPL:ModifiedBy'),
      fieldName: 'modifiedBy'
    },
    {
      title: t('Reports:Status'),
      fieldName: 'status'
    }
  ]

  React.useEffect(() => {
    showGlobalLoading(isLoading)
  }, [isLoading])

  React.useEffect(() => {
    if (isFocused) {
      getProcessGuidelineDocList(pageNum, pageSize)
      pipe(
        AuthService.userInfo,
        ZIO.map((userInfo) => {
          setUserEmail(userInfo.isGaLogin ? userInfo.name : userInfo.email)
          return userInfo
        }),
        ErrorHandling.run()
      )
    }

    return () => {
      guidelineDocFieldArr.remove()
      setValue(`guidelineDoclist`, [])
    }
  }, [isFocused])

  const getProcessGuidelineDocList = (pageN: number, pageS: number) => {
    const searchFormData = getValues()
    const fromDate = searchFormData.from
      ? PulseOpsFormat.serviceInquiry_StartOfDate(searchFormData.from, true).toISOString()
      : ''
    const toDate = searchFormData.to
      ? PulseOpsFormat.serviceInquiry_endOfDate(searchFormData.to, true).toISOString()
      : ''
    const queryData: CSService.GADDocumentListRequest = {
      code: '',
      documentType: props.processType,
      fileName: '',
      gaCode: '',
      csArea: '',
      docId: searchFormData.documentTypeDetail?.value || '',
      createdDate: '',
      createdBy: '',
      modifiedDate: '',
      modifiedBy: '',
      status: '',
      transactionType: '',
      docNo: '',
      start: pageN,
      size: pageS,
      fromDate: fromDate,
      toDate: toDate,
      docTypeCode: searchFormData.docID?.value || ''
    }
    pipe(
      CSService.getProcessGuidelineDocList(queryData),
      ZIO.map((response) => {
        if (response && response.body.length > 0) {
          const customedData = response.body.map((item) => ({
            ...item,
            activityDate: item.activityDate
              ? PulseOpsFormat.dateStringtoFormat(item.activityDate, 'DD/MM/YYYY - HH:mm:ss')
              : '',
            modifiedDate: item.modifiedDate
              ? PulseOpsFormat.dateStringtoFormat(item.modifiedDate, 'DD/MM/YYYY - HH:mm:ss')
              : '',
            // docId: pipe(
            //   O.fromNullable(docTypeDetailList.find((x) => x.docId == item.docId)),
            //   O.fold(
            //     () => '',
            //     (docItem) => docItem.name
            //   )
            // ),
            effectiveDate: item.effectiveDate
              ? PulseOpsFormat.dateStringtoFormat(item.effectiveDate, 'DD/MM/YYYY')
              : '',
            // docTypeCode: pipe(
            //   O.fromNullable(docTypeList.find((x) => x.code == item.docTypeCode)),
            //   O.fold(
            //     () => '',
            //     (docItem) => docItem.name
            //   )
            // )
          }))
          setProcessGuidelineDocList(customedData)
          initProcessGuidelineDocListForm(customedData)
          setTotal(response.total)
        } else {
          setProcessGuidelineDocList([])
          setTotal(0)
        }
      }),
      bindLoader,
      ZIO.unsafeRun({})
    )
  }

  const initProcessGuidelineDocListForm = (GADDocumentArr: CSService.GADDocumentInfo[]) => {
    let i: number = 0
    guidelineDocFieldArr.remove()
    setValue(`guidelineDoclist`, [])
    setHasEnableViewDetail(false)
    setHasEnableDelete(false)
    setHasEnableExtract(false)
    setValue('isSelectedAll', false)
    while (i < GADDocumentArr.length) {
      guidelineDocFieldArr.append({
        historyID: GADDocumentArr[i].id,
        isSelectedItem: false
      })
      setValue(`guidelineDoclist.${i}.historyID`, GADDocumentArr[i].id)
      setValue(`guidelineDoclist.${i}.isSelectedItem`, false)
      i++
    }
  }

  const onSelectedAllEvent = (selectedAllValue: boolean, arrlength: number) => {
    let i: number = 0
    while (i < arrlength) {
      if (watch(`guidelineDoclist.${i}.isSelectedItem`) !== selectedAllValue) {
        setValue(`guidelineDoclist.${i}.isSelectedItem`, selectedAllValue)
      }
      setValue(`guidelineDoclist.${i}.historyID`, processGuidelineDocList[i].id)
      i++
    }
    if (arrlength <= 10 && selectedAllValue) {
      setHasEnableExtract(true)
    } else {
      setHasEnableExtract(false)
    }
    selectedAllValue ? setHasEnableDelete(true) : setHasEnableDelete(false)
    setHasEnableViewDetail(false)
  }

  const onSelectedItemEvent = () => {
    const processGuidelineDocArr = watch(`guidelineDoclist`)
    if (processGuidelineDocArr && processGuidelineDocArr.length > 0) {
      const hasUnSelectedAll = processGuidelineDocArr.some((p) => !p.isSelectedItem)
      const hasSelectecItem = processGuidelineDocArr.some((p) => p.isSelectedItem)
      const selectedListLength = processGuidelineDocArr.filter((p) => p.isSelectedItem).length
      if (watch(`isSelectedAll`) !== !hasUnSelectedAll) {
        setValue('isSelectedAll', !hasUnSelectedAll)
      }
      hasSelectecItem ? setHasEnableDelete(true) : setHasEnableDelete(false)
      selectedListLength === 1 ? setHasEnableViewDetail(true) : setHasEnableViewDetail(false)
      selectedListLength <= 10 && selectedListLength >= 1 ? setHasEnableExtract(true) : setHasEnableExtract(false)
    }
  }

  const onPageChange = (pageN: number, pageSi: number) => {
    setPageNum(pageN)
    getProcessGuidelineDocList(pageN, pageSi)
  }

  const onRowsPerPageChange = (pageN: number, pageSi: number) => {
    setPageSize(pageSi)
    getProcessGuidelineDocList(pageN, pageSi)
  }
  const onSearchEvent = async () => {
    const isValidForm = await trigger()
    if (isValidForm) {
      setPageNum(0)
      setPageSize(10)
      getProcessGuidelineDocList(0, 10)
    }
  }

  const onClickBtnDeleteEvent = () => {
    setIsOpenDeleteModal(true)
  }

  const onChangeDocIDDocumentTypeEvent = (docTypeCode: string) => {
    setDocTypeDetailArr(docTypeDetailList.filter((p) => p.docTypeCode === docTypeCode))
    setValue(`documentTypeDetail`, undefined)
  }

  const getUpdatedData = () => {
    const guidelineDocArrArr = watch(`guidelineDoclist`)
    const updateItems = guidelineDocArrArr
      .filter(
        (x) =>
          x.isSelectedItem &&
          processGuidelineDocList.some(
            (p) => p.id === x.historyID && p.status !== OPLDocumentConst.OPLDocumentStatusConst.Inactive
          )
      )
      .map((item) => {
        return {
          id: item.historyID,
          modifiedDate: new Date().toISOString(),
          modifiedBy: userEmail,
          status: OPLDocumentConst.OPLDocumentStatusConst.Inactive
        }
      })
    const updatedData: CSService.DeleteADDocumentListRequest = {
      body: updateItems
    }
    return updatedData
  }

  const onConfirmDeleteEvent = () => {
    const deletedData = getUpdatedData()
    pipe(
      deletedData.body && deletedData.body.length <= 0
        ? ZIO.succeed({
            status: {
              message: 'MS020085'
            }
          })
        : CSService.deleteGADDocumentList(deletedData),
      ZIO.map((response) => {
        showToast(t('message:MS020090'), 'success')
        getProcessGuidelineDocList(pageNum, pageSize)
        setIsOpenDeleteModal(false)
      }),
      ZIO.mapError((error) => {
        showToast(t('message:(MS020080'), 'error')
        setIsOpenDeleteModal(false)
        return error
      }),
      bindLoader,
      ZIO.unsafeRun({})
    )
  }

  const getActiveData = () => {
    const guidelineDocArr = watch(`guidelineDoclist`)
    const currentDate = new Date().toISOString()
    const updateItems = guidelineDocArr
      .filter(
        (x) =>
          x.isSelectedItem &&
          processGuidelineDocList.some(
            (p) => p.id === x.historyID && p.status !== OPLDocumentConst.OPLDocumentStatusConst.Active
          )
      )
      .map((item) => {
        return {
          id: item.historyID,
          modifiedDate: currentDate,
          modifiedBy: userEmail,
          activityDate: currentDate,
          status: OPLDocumentConst.OPLDocumentStatusConst.Active
        }
      })
    const updatedData: CSService.UpdateADDocumentListRequest = {
      body: updateItems
    }
    return updatedData
  }

  const onActiveEvent = () => {
    const activeData = getActiveData()
    pipe(
      activeData.body && activeData.body.length <= 0
        ? ZIO.succeed({
            status: {
              message: 'MS020085'
            }
          })
        : CSService.updateGADDocumentList(activeData),
      ZIO.map((response) => {
        getProcessGuidelineDocList(pageNum, pageSize)
        setIsOpenDeleteModal(false)
      }),
      ZIO.mapError((error) => {
        showToast(t('message:(MS020080'), 'error')
        setIsOpenDeleteModal(false)
        return error
      }),
      bindLoader,
      ZIO.unsafeRun({})
    )
  }
  const onViewDetailEvent = () => {
    const GADDocumentArr = watch(`guidelineDoclist`)
    const fileUrl = pipe(
      GADDocumentArr.find((p) => p.isSelectedItem),
      O.fromNullable,
      O.map((documentItem) => {
        return pipe(
          processGuidelineDocList.find((x) => x.id === documentItem.historyID),
          O.fromNullable,
          O.fold(
            () => '',
            (item) => item.urlFilenet
          )
        )
      }),
      O.getOrElse(() => '')
    )
    if (!!fileUrl) {
      pipe(
        ZIO.zipPar(AuthService.token, AuthService.getLoginType),
        ZIO.flatMap(([token, loginType]) => {
          return ZIO.fromPromise(() =>
            fetch(fileUrl, {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Authen-Vendor': loginType
              }
            })
          )
        }),
        ZIO.flatMap((res) =>
          ZIO.zipPar(
            ZIO.succeed(res),
            ZIO.fromPromise(() => res.blob())
          )
        ),
        ZIO.map(([res, blob]) => {
          window.open(window.URL.createObjectURL(blob), '_blank')
        }),
        bindLoader,
        ZIO.unsafeRun({})
      )
    }
  }
  const onExtractFileListEvent = () => {
    const signatureArr = watch(`guidelineDoclist`)
    const extractList = processGuidelineDocList.filter((p) =>
      signatureArr.some((x) => x.historyID === p.id && x.isSelectedItem)
    )
    pipe(
      extractList,
      A.map((item) => {
        const fileUrl = item.urlFilenet
        return pipe(
          ZIO.zipPar(AuthService.token, AuthService.getLoginType),
          ZIO.flatMap(([token, loginType]) => {
            return ZIO.fromPromise(() =>
              fetch(fileUrl, {
                method: 'GET',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'X-Authen-Vendor': loginType
                }
              })
            )
          }),
          ZIO.flatMap((res) =>
            ZIO.zipPar(
              ZIO.succeed(res),
              ZIO.fromPromise(() => res.blob())
            )
          ),
          ZIO.map(([res, blob]) => {
            const type = res.headers.get('content-type') || ''
            OPLCommon.downloadFileByURI(window.URL.createObjectURL(blob), `file.${type.split('/')[1]}`)
          }),
          bindLoader,
          ZIO.unsafeRun({})
        )
      })
    )
  }
  return (
    <ScrollView style={OPLSharedStyles.container} horizontal={false}>
      <SectionContent>
        <SectionRow>
          <SectionCol>
            <Controller
              control={control}
              name={`docID`}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <SelectSearch
                  label={t('OPL_CS:CS:DocIDDocumentType')}
                  options={docTypeList.map((item) => ({ label: item.name, value: item.code }))}
                  value={value}
                  onChange={(val) => {
                    onChange(val)
                    onChangeDocIDDocumentTypeEvent(val?.value || '')
                  }}
                  popupIcon={<assets.ArrowDownDropdownIcon />}
                  placeholder={t('common:Select')}
                />
              )}
            />
          </SectionCol>
          <SectionCol>
            <Controller
              control={control}
              name={`documentTypeDetail`}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <SelectSearch
                  label={t('OPL_CS:CS:DocumentTypeDetail')}
                  options={docTypeDetailArr.map((item) => ({
                    label: item.name,
                    value: item.docId
                  }))}
                  value={value}
                  onChange={(val) => {
                    onChange(val)
                  }}
                  popupIcon={<assets.ArrowDownDropdownIcon />}
                  disabled={!watch(`docID`) || !watch(`docID`)?.value}
                  placeholder={t('common:Select')}
                />
              )}
            />
          </SectionCol>
          <SectionCol>
            <Controller
              control={control}
              name="from"
              rules={{
                validate: (val) => {
                  if (!val && !watch(`to`)) {
                    return true
                  } else if (!!val && !moment(val).isValid()) {
                    return `${t('message:MS020081')}`
                  } else if (!!val && moment(val).valueOf() > moment(new Date()).valueOf()) {
                    return `${t('message:MS990032')}`
                  } else if (!val && !!watch(`to`)) {
                    return `${t('message:MS020009', { field: t('OPL_CS:OPL:From') })}`
                  } else {
                    return true
                  }
                }
              }}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <DatePicker
                  alwaysShow
                  label={t('OPL_CS:OPL:From')}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  maxDate={new Date()}
                  maxDateMessage={t('message:MS990032')}
                  minDate={undefined}
                  minDateMessage={''}
                  errorMessage={error?.message}
                  labelStyle={OPLSharedStyles.datePickerText}
                />
              )}
            />
          </SectionCol>
          <SectionCol sectionStyles={OPLSharedStyles.secondLine}>
            <Controller
              control={control}
              name="to"
              rules={{
                validate: (val) => {
                  if (!val && !watch(`from`)) {
                    return true
                  } else if (!!val && !moment(val).isValid()) {
                    return `${t('message:MS020081')}`
                  } else if (!!val && moment(val).valueOf() > moment(new Date()).valueOf()) {
                    return `${t('message:MS990032')}`
                  } else if (!val && !!watch(`from`)) {
                    return `${t('message:MS020009', { field: t('OPL_CS:OPL:To') })}`
                  } else if (moment(val).valueOf() < moment(watch(`from`)).valueOf()) {
                    return `${t('message:MS030044')}`
                  } else {
                    return true
                  }
                }
              }}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <DatePicker
                  alwaysShow
                  label={t('OPL_CS:OPL:To')}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  maxDate={new Date()}
                  maxDateMessage={t('message:MS990032')}
                  minDate={undefined}
                  errorMessage={error?.message}
                  labelStyle={OPLSharedStyles.datePickerText}
                />
              )}
            />
          </SectionCol>
        </SectionRow>
        {userPermissions.includes(Permission.SearchInternalProcessGuidelineslist) && (
          <View style={[OPLSharedStyles.buttonContainer, OPLSharedStyles.secondLine]}>
            <TouchableOpacity
              style={[sharedStyle.button, sharedStyle.btnRed, OPLSharedStyles.buttonContent]}
              onPress={() => onSearchEvent()}
            >
              <Text style={sharedStyle.textButtonRed}>{t('common:Search')}</Text>
            </TouchableOpacity>
          </View>
        )}
      </SectionContent>
      <CheckboxTable
        displayedColumns={displayedColumns}
        dataSource={processGuidelineDocList}
        stickyHeader
        isPagination={true}
        totalRecord={total}
        pageSize={pageSize}
        pageNum={pageNum}
        setPageNum={setPageNum}
        setPageSize={setPageSize}
        setTotalRecord={setTotal}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sortItem={sortItem}
        setSortItem={setSortItem}
      />
      <View style={[OPLSharedStyles.buttonContainer, OPLSharedStyles.footerContainer]}>
        {userPermissions.includes(Permission.DeleteInternalProcessGuidelineslist) && (
          <TouchableOpacity
            style={[
              sharedStyle.button,
              hasEnableDelete ? sharedStyle.btnRed : sharedStyle.btnDisabled,
              OPLSharedStyles.buttonContent
            ]}
            onPress={() => onClickBtnDeleteEvent()}
            disabled={!hasEnableDelete}
          >
            <Text style={[hasEnableDelete ? sharedStyle.textButtonRed : sharedStyle.btnTextDisabled]}>
              {t('claim:Delete')}
            </Text>
          </TouchableOpacity>
        )}
        {userPermissions.includes(Permission.ActiveInternalProcessGuidelineslist) && (
          <TouchableOpacity
            style={[
              sharedStyle.button,
              hasEnableDelete ? sharedStyle.btnRed : sharedStyle.btnDisabled,
              OPLSharedStyles.buttonContent
            ]}
            onPress={() => onActiveEvent()}
            disabled={!hasEnableDelete}
          >
            <Text style={[hasEnableDelete ? sharedStyle.textButtonRed : sharedStyle.btnTextDisabled]}>
              {t('OPL_CS:CS:Active')}
            </Text>
          </TouchableOpacity>
        )}
        {userPermissions.includes(Permission.ViewdetailInternalProcessGuidelineslist) && (
          <TouchableOpacity
            style={[
              sharedStyle.button,
              hasEnableViewDetail ? sharedStyle.btnRed : sharedStyle.btnDisabled,
              OPLSharedStyles.buttonContent
            ]}
            onPress={() => onViewDetailEvent()}
            disabled={!hasEnableViewDetail}
          >
            <Text style={[hasEnableViewDetail ? sharedStyle.textButtonRed : sharedStyle.btnTextDisabled]}>
              {t('OPL_CS:OPL:ViewDetail')}
            </Text>
          </TouchableOpacity>
        )}
        {userPermissions.includes(Permission.ExtractInternalProcessGuidelineslist) && (
          <TouchableOpacity
            style={[
              sharedStyle.button,
              hasEnableExtract ? sharedStyle.btnRed : sharedStyle.btnDisabled,
              OPLSharedStyles.buttonContent
            ]}
            disabled={!hasEnableExtract}
            onPress={() => onExtractFileListEvent()}
          >
            <Text style={[hasEnableExtract ? sharedStyle.textButtonRed : sharedStyle.btnTextDisabled]}>
              {t('OPL_CS:OPL:Extract')}
            </Text>
          </TouchableOpacity>
        )}
      </View>
      <OPLDeleteModal
        headerTitle={t('OPL_CS:OPL:Notice')}
        message={t('message:MS020066')}
        isVisiable={isOpenDeleteModal}
        onCloseModal={() => setIsOpenDeleteModal(false)}
        onCancelEvent={() => setIsOpenDeleteModal(false)}
        onYesEvent={onConfirmDeleteEvent}
      ></OPLDeleteModal>
    </ScrollView>
  )
}
